/**
 * ClickUp API Cache
 *
 * - TTL: 5 минут для большинства данных
 * - TTL: 30 секунд для inbox events
 * - TTL: 2 минуты для деталей задачи
 * - Дедупликация in-flight запросов
 * - Инвалидация при мутациях
 * - Очистка при выходе с ClickUp-страниц
 */

import { clickupApi } from './clickup';
import type {
    ClickUpTeam,
    ClickUpSpace,
    ClickUpList,
    ClickUpListStatus,
    ClickUpTask,
    ClickUpTaskDetail,
    ClickUpTaskComment,
    ClickUpInboxEvent,
    ClickUpSettings,
} from './clickup';

const CACHE_TTL_DEFAULT = 5 * 60 * 1000;  // 5 минут
const CACHE_TTL_INBOX = 30 * 1000;       // 30 секунд
const CACHE_TTL_TASK = 2 * 60 * 1000;  // 2 минуты

interface CacheEntry<T> {
    data: T;
    expiresAt: number;
}

// Глобальный singleton кэш
const store: { [key: string]: CacheEntry<any> } = {};
const inflight: { [key: string]: Promise<any> } = {};

function isValid(key: string): boolean {
    const entry = store[key];
    return !!entry && Date.now() < entry.expiresAt;
}

async function cached<T>(key: string, ttl: number, fetcher: () => Promise<T>): Promise<T> {
    // 1. Есть свежий кэш?
    if (isValid(key)) {
        return store[key].data as T;
    }

    // 2. Уже выполняется запрос с тем же ключом?
    if (inflight[key] !== undefined) {
        return inflight[key] as Promise<T>;
    }

    // 3. Делаем новый запрос
    const promise = fetcher()
        .then((data) => {
            store[key] = { data, expiresAt: Date.now() + ttl };
            delete inflight[key];
            return data;
        })
        .catch((err) => {
            delete inflight[key];
            throw err;
        });

    inflight[key] = promise;
    return promise;
}

/** Сбросить конкретный ключ */
function invalidate(key: string) {
    delete store[key];
    delete inflight[key];
}

/** Сбросить все ключи с определённым префиксом */
function invalidateByPrefix(prefix: string) {
    Object.keys(store).forEach((key) => {
        if (key.indexOf(prefix) === 0) delete store[key];
    });
    Object.keys(inflight).forEach((key) => {
        if (key.indexOf(prefix) === 0) delete inflight[key];
    });
}

/** Инвалидировать все inbox-ключи */
function invalidateAllInbox() {
    invalidateByPrefix('inbox:');
}

/** Полная очистка — вызывается при уходе с /clickup/* */
export function clearClickUpCache() {
    Object.keys(store).forEach((k) => delete store[k]);
    Object.keys(inflight).forEach((k) => delete inflight[k]);
}

/** Инвалидировать конкретную задачу и её комментарии */
export function invalidateTask(taskId: string) {
    invalidate(`task:${taskId}`);
    invalidate(`comments:${taskId}`);
}

// ==========================================
// CACHED API OBJECT
// ==========================================

export const cachedClickupApi = {
    /* ---------- READ — кэшируем ---------- */

    getSettings: (): Promise<ClickUpSettings> =>
        cached('settings', CACHE_TTL_DEFAULT, () => clickupApi.getSettings()),

    getTeams: (): Promise<{ teams: ClickUpTeam[] }> =>
        cached('teams', CACHE_TTL_DEFAULT, () => clickupApi.getTeams()),

    getSpaces: (teamId: string): Promise<{ spaces: ClickUpSpace[] }> =>
        cached(`spaces:${teamId}`, CACHE_TTL_DEFAULT, () => clickupApi.getSpaces(teamId)),

    getLists: (spaceId: string): Promise<{ lists: ClickUpList[] }> =>
        cached(`lists:${spaceId}`, CACHE_TTL_DEFAULT, () => clickupApi.getLists(spaceId)),

    getListStatuses: (listId: string): Promise<{ statuses: ClickUpListStatus[]; count: number }> =>
        cached(`statuses:${listId}`, CACHE_TTL_DEFAULT, () => clickupApi.getListStatuses(listId)),

    getListTasks: (listId: string, status?: string, assignee?: string, page = 0): Promise<{ tasks: ClickUpTask[]; count: number }> => {
        const key = `tasks:${listId}:${status || ''}:${assignee || ''}:${page}`;
        return cached(key, CACHE_TTL_DEFAULT, () => clickupApi.getListTasks(listId, status, assignee, page));
    },

    getInboxEvents: (teamId: string, limitTasks = 50, perTaskComments = 10): Promise<{
        events: ClickUpInboxEvent[];
        unread_tasks: number;
        unread_events: number;
        read_state: Record<string, number>;
    }> =>
        cached(`inbox:${teamId}`, CACHE_TTL_INBOX, () =>
            clickupApi.getInboxEvents(teamId, limitTasks, perTaskComments)
        ),

    getTask: (taskId: string): Promise<{ task: ClickUpTaskDetail }> =>
        cached(`task:${taskId}`, CACHE_TTL_TASK, () => clickupApi.getTask(taskId)),

    getTaskComments: (taskId: string): Promise<{ comments: ClickUpTaskComment[]; count: number }> =>
        cached(`comments:${taskId}`, CACHE_TTL_TASK, () => clickupApi.getTaskComments(taskId)),

    /* ---------- WRITE — инвалидируем после ---------- */

    saveApiKey: async (apiKey: string) => {
        const res = await clickupApi.saveApiKey(apiKey);
        clearClickUpCache(); // новый ключ — совсем другие данные
        return res;
    },

    removeApiKey: async () => {
        const res = await clickupApi.removeApiKey();
        clearClickUpCache();
        return res;
    },

    markInboxTaskRead: async (taskId: string, timestampMs?: number) => {
        const res = await clickupApi.markInboxTaskRead(taskId, timestampMs);
        invalidateAllInbox();
        return res;
    },

    addTaskComment: async (taskId: string, commentText: string, assigneeId?: number) => {
        const res = await clickupApi.addTaskComment(taskId, commentText, assigneeId);
        invalidate(`comments:${taskId}`);
        invalidateAllInbox();
        return res;
    },

    updateTask: async (taskId: string, data: Parameters<typeof clickupApi.updateTask>[1]) => {
        const res = await clickupApi.updateTask(taskId, data);
        if (res?.task) {
            // Обновляем кэш актуальными данными — не нужно перезапрашивать
            store[`task:${taskId}`] = { data: { task: res.task }, expiresAt: Date.now() + CACHE_TTL_TASK };
        } else {
            invalidate(`task:${taskId}`);
        }
        invalidateByPrefix('tasks:'); // списки задач тоже устарели
        return res;
    },

    updateTaskStatus: async (taskId: string, status: string) => {
        const res = await clickupApi.updateTaskStatus(taskId, status);
        if (res?.task) {
            store[`task:${taskId}`] = { data: { task: res.task }, expiresAt: Date.now() + CACHE_TTL_TASK };
        } else {
            invalidate(`task:${taskId}`);
        }
        invalidateByPrefix('tasks:');
        invalidateAllInbox();
        return res;
    },

    addTimeEntry: async (taskId: string, teamId: string, durationMinutes: number, description?: string) => {
        const res = await clickupApi.addTimeEntry(taskId, teamId, durationMinutes, description);
        invalidate(`task:${taskId}`);
        return res;
    },

    startTimeTracker: async (taskId: string, teamId: string) => {
        const res = await clickupApi.startTimeTracker(taskId, teamId);
        invalidate(`task:${taskId}`);
        return res;
    },

    stopTimeTracker: async (teamId: string) => {
        const res = await clickupApi.stopTimeTracker(teamId);
        invalidateByPrefix('task:'); // не знаем какая задача была активна
        return res;
    },

    /* ---------- Остальные — прокидываем напрямую ---------- */
    deleteTaskComment: clickupApi.deleteTaskComment,
    updateTaskComment: clickupApi.updateTaskComment,
    addCommentReply: clickupApi.addCommentReply,
    addCommentReaction: clickupApi.addCommentReaction,
    removeCommentReaction: clickupApi.removeCommentReaction,
    getSubtasks: clickupApi.getSubtasks,
    createSubtask: clickupApi.createSubtask,
    updateSubtask: clickupApi.updateSubtask,
    deleteSubtask: clickupApi.deleteSubtask,
    getChecklists: clickupApi.getChecklists,
    createChecklist: clickupApi.createChecklist,
    deleteChecklist: clickupApi.deleteChecklist,
    getChecklistItems: clickupApi.getChecklistItems,
    createChecklistItem: clickupApi.createChecklistItem,
    updateChecklistItem: clickupApi.updateChecklistItem,
    deleteChecklistItem: clickupApi.deleteChecklistItem,
    getAttachments: clickupApi.getAttachments,
    uploadAttachment: clickupApi.uploadAttachment,
    deleteAttachment: clickupApi.deleteAttachment,
    getDependencies: clickupApi.getDependencies,
    addDependency: clickupApi.addDependency,
    removeDependency: clickupApi.removeDependency,
    getActivity: clickupApi.getActivity,
    getReminders: clickupApi.getReminders,
    createReminder: clickupApi.createReminder,
    deleteReminder: clickupApi.deleteReminder,
    getTags: clickupApi.getTags,
    addTag: clickupApi.addTag,
    removeTag: clickupApi.removeTag,
    getWatchers: clickupApi.getWatchers,
    addWatcher: clickupApi.addWatcher,
    removeWatcher: clickupApi.removeWatcher,
    updateCustomField: clickupApi.updateCustomField,
    getUser: clickupApi.getUser,
    getInbox: clickupApi.getInbox,
};

/** Debug helper: узнать что сейчас в кэше */
export function getCacheStats(): Array<{ key: string; ttlMs: number; fresh: boolean }> {
    const now = Date.now();
    return Object.keys(store).map((key) => ({
        key,
        ttlMs: store[key].expiresAt - now,
        fresh: now < store[key].expiresAt,
    }));
}
